import { BarSegment, useChart } from '@chakra-ui/charts';
import { Box, HStack, Heading, SimpleGrid, Skeleton, Stat } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';

import { type IndicatorsStat, StatsService } from '@/client';

const hashTextToNumber = (text: string) => {
  const hash = text.split('').reduce((acc, char) => {
    return (acc << 5) - acc + char.charCodeAt(0); // Simple hash function
  }, 0);
  return Math.abs(hash);
};

const getColorForText = (text: string, shade = '500') => {
  const colors = ['teal', 'purple', 'blue', 'green', 'red', 'yellow', 'orange', 'pink'];
  const hashValue = hashTextToNumber(text);
  const colorIndex = hashValue % colors.length;
  return `${colors[colorIndex]}.${shade}`;
};

const StatsCards = ({ statsData, isLoading }: { statsData: IndicatorsStat | undefined; isLoading: boolean }) => {
  if (!statsData) {
    return null;
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} px={4}>
        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Nombre d'indicateurs générals
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.general_data_count} / {statsData.indicators_count}
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(((statsData.general_data_count as number) / (statsData.indicators_count as number)) * 100)}{' '}
                % des actions sont générals
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>

        <Stat.Root borderWidth="1px" p="4" rounded="md" bg="primary.500">
          <Stat.Label fontSize="lg" color={'white'}>
            Nombre d'indicateurs confidentiels
          </Stat.Label>
          <Skeleton width="full" loading={isLoading}>
            <HStack>
              <Stat.ValueText textWrap={'nowrap'} fontSize="3xl" color={'white'}>
                {statsData.confidential_count} / {statsData.indicators_count}
              </Stat.ValueText>
              <Stat.HelpText color={'white'}>
                {Math.floor(((statsData.confidential_count as number) / (statsData.indicators_count as number)) * 100)}{' '}
                % des actions sont confidentiels
              </Stat.HelpText>
            </HStack>
          </Skeleton>
        </Stat.Root>
      </SimpleGrid>
    </>
  );
};

const BarSegmentsDistribution = ({ segments, title }: { segments: [string, number][]; title: string }) => {
  const chart = useChart({
    sort: { by: 'value', direction: 'desc' },
    data: segments.map(([name, value]) => ({
      name,
      value,
      color: getColorForText(name),
    })),
  });

  return (
    <Box>
      <Heading size={'lg'} mb={4}>
        {title}
      </Heading>
      <BarSegment.Root chart={chart} barSize="6">
        <BarSegment.Content>
          <BarSegment.Bar gap="0.5" tooltip />
        </BarSegment.Content>
        <BarSegment.Legend showPercent />
      </BarSegment.Root>
    </Box>
  );
};

function getIndicatorsStatsQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['stats', { slug }],
    queryFn: async () => {
      return await StatsService.readIndicatorsStats({
        companySlug: slug,
      });
    },
  };
}

export const DashboardIndicatorsView = () => {
  const { slug } = useParams({ strict: false });

  const { data: statsData, isLoading } = useQuery({
    ...getIndicatorsStatsQueryOptions({
      slug,
    }),
  });

  return (
    <>
      <StatsCards statsData={statsData} isLoading={isLoading} />
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="4" p="4">
        <BarSegmentsDistribution
          segments={statsData?.departments || []}
          title="Distributions des indicateurs par departements"
        />
        <BarSegmentsDistribution segments={statsData?.units || []} title="Distributions des indicateurs par unités" />
      </SimpleGrid>
    </>
  );
};

export default DashboardIndicatorsView;
